/**
 * THE ONE STYLESHEET FOR THE REWARD AND LOADING MOMENTS.
 *
 * Convention, matched from `match/render/hud.ts` and `a11y/apply.ts`: a module
 * exports its CSS as a string and injects a single `<style>` into the document.
 * No bundler CSS import — these components are mounted by screens in three
 * different chunks and must not drag a stylesheet graph across the ADR-0005
 * split.
 *
 * ── REF-COUNTED, BECAUSE FOUR COMPONENTS SHARE IT ───────────────────────────
 * A loading screen, a daily popup, a crowns reveal and a chest can all be alive
 * at once (the post-match flow is literally crowns then chest). One `<style>`
 * serves all of them, and the LAST teardown removes it. A naive
 * inject/remove-per-mount unstyles the components still on screen; a
 * never-remove leaks a node into every test's document.
 *
 * ── MOTION POLICY (docs/UI-STYLE.md) ────────────────────────────────────────
 * "Idle motion on the things a player is meant to want: the chest jumps and
 * turns on the spot. Reward and currency elements move; structural chrome does
 * not." So every `@keyframes` in this file is applied ONLY to a reward subject
 * — the chest, a reward icon, a sigil pip, the loading pips. No panel, no row,
 * no header, and nothing behind the match ever animates. Every animation is
 * switched off wholesale by the `prefers-reduced-motion` block at the end AND
 * by the `is-reduced-motion` class the components set when the caller has
 * already resolved the preference.
 */
import { PRESS_CSS } from '../ui/press';

const STYLE_ID = 'rga-rewards-css';

export const REWARDS_CSS = `
${PRESS_CSS}

/* ── shared ─────────────────────────────────────────────────────────── */
.rga-rw{color:var(--color-text);font-family:var(--font-system,system-ui,sans-serif)}
.rga-rw *{box-sizing:border-box}
.rga-rw__heading{margin:0;font-size:1.15rem;font-weight:800;letter-spacing:.01em}
.rga-rw__sub{margin:.25rem 0 0;font-size:.85rem;color:var(--color-text-dim)}
/* Cartoon register: chunky rim, volume, a top-light. Never a flat rectangle. */
.rga-rw__cta{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;
  min-height:44px;min-width:44px;padding:0 1.15rem;border:0;border-radius:12px;cursor:pointer;
  font:inherit;font-weight:800;font-size:.95rem;color:var(--color-ink);
  background:linear-gradient(var(--color-gold-bright),color-mix(in srgb,var(--color-gold-bright) 70%,var(--color-gold)));
  box-shadow:0 4px 0 color-mix(in srgb,var(--color-gold) 55%,#000),0 8px 14px rgba(0,0,0,.28),inset 0 2px 0 rgba(255,255,255,.45)}
.rga-rw__cta[aria-disabled=true]{cursor:default;filter:saturate(.35)}

/* ── loading screen ─────────────────────────────────────────────────── */
.rga-load{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;
  min-height:min(70dvh,26rem);padding:2rem 1.25rem;text-align:center;
  background:radial-gradient(120% 80% at 50% 12%,var(--color-surface) 0%,var(--color-bg) 72%)}
.rga-load[data-splash]{position:relative;background:var(--color-bg);overflow:hidden}
/* THE SCRIM IS ALSO THE CONTRAST FLOOR. The pips and the bar now sit in normal
   flow over this art instead of on a dark plate, and they carry real progress
   information, so STYLE_BIBLE section 4 wants a 3:1 non-text minimum against a
   ground this module cannot measure. The gradient ends at .94 over the band
   they occupy, which makes that ground knowable whatever the splash shows. */
.rga-load[data-splash]::before{content:"";position:absolute;inset:0;z-index:0;
  background-image:linear-gradient(180deg,rgba(20,34,74,.45) 0%,rgba(20,34,74,.66) 52%,rgba(20,34,74,.94) 100%),url("/ui/loading/loading-splash.webp");
  background-size:cover;background-position:center 22%;background-repeat:no-repeat}
.rga-load[data-splash]>*{position:relative;z-index:1}
.rga-load[data-splash] .rga-load__bar{width:min(80vw,22rem);height:14px}

/* THE HERO STANDS ON THE SPLASH — no plate, no frame, no placeholder box.
   A transparent model cut-out carries its own silhouette shadow, so the wait
   reads as art rather than as a slot that art has not arrived in yet. The BOX
   IS RESERVED BY THIS RULE and never by the image, so the picture landing
   moves nothing; and with no image at all the reserved box is an empty patch
   of splash, which is the finished look rather than a hatched rectangle. */
.rga-load__hero{position:relative;display:flex;align-items:flex-end;justify-content:center;
  width:min(86vw,24rem);height:min(38vh,17rem);pointer-events:none}
/* The ground it stands on. Hidden until the cut-out is decoded, or a lone
   ellipse of shadow floats on the art with nothing above it. */
.rga-load__hero::after{content:"";position:absolute;left:50%;bottom:-2px;
  width:68%;height:26px;margin-left:-34%;border-radius:50%;
  opacity:0;transition:opacity 260ms ease-out;
  background:radial-gradient(closest-side,rgba(14,26,66,.5),rgba(14,26,66,0))}
.rga-load__hero[data-loaded=true]::after{opacity:1}
/* Only a DECODED image is ever visible: a 404 removes the node outright, so
   the plate can never degrade to a broken-image glyph. */
.rga-load__hero-img{max-width:100%;max-height:100%;width:auto;height:100%;object-fit:contain;
  opacity:0;transform:translateY(10px);
  transition:opacity 280ms ease-out,transform 280ms ease-out;
  filter:drop-shadow(0 16px 22px rgba(14,26,66,.55))}
.rga-load__hero[data-loaded=true] .rga-load__hero-img{opacity:1;transform:translateY(0)}

.rga-load__pips{display:flex;justify-content:center;gap:.6rem}
.rga-load__pip{width:14px;height:14px;border-radius:50%;
  border:2px solid var(--color-gold);background:color-mix(in srgb,var(--color-gold) 15%,transparent)}
.rga-load__pip.is-lit{background:var(--color-gold-bright);animation:rga-rw-pip 1.2s ease-in-out infinite}
.rga-load__pip:nth-child(2).is-lit{animation-delay:.15s}
.rga-load__pip:nth-child(3).is-lit{animation-delay:.3s}
.rga-load__msg{margin:0;font-size:.95rem;font-weight:700}
/* CLIPPED, NEVER display:none. The loading screen is art and a bar and nothing
   else, but the status line is a testid contract and the only announcement a
   screen reader gets. display:none would take it out of the accessibility tree;
   this keeps it there and off the screen. */
.rga-sr-only{position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;
  overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}
.rga-load__bar{width:min(72vw,15rem);height:10px;border-radius:6px;overflow:hidden;
  border:1px solid var(--color-border);background:var(--color-surface-2);box-shadow:var(--rga-depth)}
.rga-load__fill{display:block;height:100%;width:0;border-radius:6px;
  background:linear-gradient(90deg,var(--color-gold),var(--color-gold-bright));transition:width 180ms linear}
.rga-load__failed{margin:0;font-size:.9rem;color:var(--color-error)}

/* ── daily reward: the 7-day calendar ──────────────────────────────── */
.rga-daily{display:flex;flex-direction:column;gap:.85rem;min-width:min(88vw,22rem);padding:1rem}
.rga-daily__hero{display:flex;align-items:center;justify-content:space-between;gap:.75rem;flex-wrap:wrap}
.rga-daily__flame{display:flex;align-items:center;gap:.45rem;padding:.35rem .8rem .35rem .45rem;border-radius:999px;
  background:linear-gradient(90deg,rgba(255,120,30,.28),rgba(255,184,0,.1));border:1px solid rgba(255,150,40,.55)}
.rga-daily__flame-icon{display:inline-flex;animation:rga-rw-flicker 1.4s ease-in-out infinite}
.rga-daily__flame-num{font-size:1.5rem;font-weight:900;line-height:1;color:var(--color-gold-bright);
  text-shadow:0 2px 0 rgba(0,0,0,.45),0 0 12px rgba(255,140,0,.45)}
.rga-daily__flame-label{font-size:.72rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;
  color:var(--color-text-dim)}
.rga-daily__clock{display:flex;align-items:center;gap:.35rem;font-size:.8rem;font-weight:700;
  color:var(--color-text);font-variant-numeric:tabular-nums}
.rga-daily__note{margin:0;font-size:.8rem;line-height:1.35;padding:.45rem .65rem;border-radius:10px;
  background:var(--color-surface-2);border:1px solid var(--color-border);color:var(--color-text-dim)}
.rga-daily__note[data-tone=good]{border-color:var(--color-success);color:var(--color-success)}
.rga-daily__note[data-tone=warn]{border-color:var(--color-gold);color:var(--color-gold)}
.rga-daily__days{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.5rem;padding:.15rem;perspective:800px}
.rga-daily__day{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:space-between;gap:.25rem;
  min-height:7.4rem;padding:.5rem .3rem .45rem;border-radius:var(--rga-radius-panel,14px);border:var(--rga-rim,2px) solid var(--color-border);
  background:var(--rga-face,none),var(--color-surface);text-align:center;overflow:hidden;
  box-shadow:var(--rga-lift-1),var(--rga-sheen);transform-style:preserve-3d}
.rga-daily__day[data-day="7"]{grid-column:span 2;background:var(--rga-face,none),linear-gradient(160deg,color-mix(in srgb,var(--color-gold) 24%,var(--color-surface)),var(--color-surface) 70%);
  border-color:var(--color-gold)}
.rga-daily__num{font-size:.68rem;font-weight:900;letter-spacing:.05em;text-transform:uppercase;
  color:var(--color-text-dim)}
.rga-daily__art{display:flex;align-items:center;justify-content:center;height:3.4rem;
  filter:drop-shadow(0 4px 6px rgba(0,0,0,.45))}
.rga-daily__art img{max-height:100%}
.rga-daily__day[data-day="7"] .rga-daily__art{height:4.2rem}
.rga-daily__lines{display:flex;flex-direction:column;align-items:center;gap:.1rem}
.rga-daily__amt{font-size:.8rem;font-weight:900;color:var(--color-text)}
.rga-daily__crate{font-size:.66rem;font-weight:800;line-height:1.15;color:var(--color-gold)}
.rga-daily__state{font-size:.64rem;font-weight:700;color:var(--color-text-dim)}
/* Today: a gold rim that breathes, and a ribbon that says so in words. */
.rga-daily__day[data-state=claimable]{border-color:var(--color-gold-bright);
  background:var(--rga-face,none),linear-gradient(170deg,color-mix(in srgb,var(--color-gold) 30%,var(--color-surface)),var(--color-surface));animation:rga-rw-glow 1.6s ease-in-out infinite}
.rga-daily__day[data-state=claimable] .rga-daily__art{animation:rga-rw-hop 1.8s ease-in-out infinite}
.rga-daily__day[data-state=claimable] .rga-daily__num{color:var(--color-gold-bright)}
.rga-daily__ribbon{position:absolute;top:.35rem;right:-1.9rem;transform:rotate(35deg);width:6rem;padding:.05rem 0;
  font-size:.56rem;font-weight:900;text-transform:uppercase;letter-spacing:.06em;color:var(--color-ink);
  background:linear-gradient(var(--color-gold-bright),var(--color-gold));box-shadow:0 2px 0 color-mix(in srgb,var(--color-gold) 55%,#000),0 2px 4px rgba(0,0,0,.3)}
/* State is never colour alone (STYLE_BIBLE §3): a spent day is dimmed AND stamped AND says so. */
.rga-daily__day[data-state=claimed] .rga-daily__art,.rga-daily__day[data-state=claimed] .rga-daily__lines{opacity:.45}
/* The stamp lands on the ART, never on the amount under it (layout audit:
   it covered the "60"), so the spent day still reads its number. */
.rga-daily__stamp{position:absolute;inset:30% 0 auto 0;margin:auto;display:none;align-items:center;justify-content:center;gap:.2rem;
  width:max-content;padding:.1rem .45rem;border:2px solid var(--color-success);border-radius:6px;color:var(--color-success);
  font-size:.66rem;font-weight:900;text-transform:uppercase;letter-spacing:.06em;transform:rotate(-12deg);
  background:var(--color-surface-2)}
.rga-daily__day[data-state=claimed] .rga-daily__stamp{display:flex}
.rga-daily__day.is-just-claimed .rga-daily__stamp{animation:rga-rw-stamp 420ms cubic-bezier(.2,1.6,.4,1) both}
.rga-daily__day[data-state=locked]{opacity:.8}
.rga-daily__day[data-state=locked] .rga-daily__art{filter:grayscale(.55) brightness(.75)}
.rga-daily__disc{position:relative;width:34px;height:34px;border-radius:50%;
  border:2px solid rgba(0,0,0,.4);background:radial-gradient(circle at 35% 30%,#ffe9a8,#e0a92c 62%,#a8761a)}
.rga-daily__day.is-flipping{animation:rga-rw-flip 620ms cubic-bezier(.3,1.3,.5,1) both}
.rga-daily__burst{position:absolute;inset:-30%;pointer-events:none;border-radius:50%;
  background:repeating-conic-gradient(rgba(255,214,105,.55) 0 10deg,transparent 10deg 30deg);
  -webkit-mask:radial-gradient(circle,#000 20%,transparent 66%);mask:radial-gradient(circle,#000 20%,transparent 66%);
  animation:rga-rw-burst 900ms ease-out both}
.rga-daily__first{display:flex;align-items:center;gap:.6rem;padding:.5rem .7rem;border-radius:12px;
  border:1px dashed rgba(255,184,0,.45);background:rgba(255,184,0,.06)}
.rga-daily__first[data-earned=true]{border-style:solid;border-color:rgba(76,201,120,.55);background:rgba(76,201,120,.08)}
.rga-daily__first-text{display:flex;flex-direction:column;gap:.1rem;font-size:.78rem}
.rga-daily__first-title{font-weight:900;color:var(--color-text)}
.rga-daily__first-sub{color:var(--color-text-dim)}
.rga-daily__foot{display:flex;flex-direction:column;align-items:stretch;gap:.4rem;
  padding-top:.6rem;border-top:1px solid var(--color-border)}
.rga-daily__error{margin:0;font-size:.8rem;color:var(--color-error)}
@keyframes rga-rw-glow{0%,100%{box-shadow:0 0 0 2px rgba(255,184,0,.25),0 0 10px rgba(255,184,0,.25)}
  50%{box-shadow:0 0 0 3px rgba(255,184,0,.55),0 0 22px rgba(255,184,0,.55)}}
@keyframes rga-rw-flicker{0%,100%{transform:scale(1) rotate(0)}30%{transform:scale(1.08) rotate(-3deg)}
  60%{transform:scale(.96) rotate(2deg)}}
@keyframes rga-rw-flip{0%{transform:rotateY(0)}50%{transform:rotateY(90deg) scale(1.06)}100%{transform:rotateY(0)}}
@keyframes rga-rw-burst{0%{transform:scale(.3) rotate(0);opacity:0}25%{opacity:1}100%{transform:scale(1.25) rotate(40deg);opacity:0}}
@keyframes rga-rw-stamp{0%{transform:rotate(-12deg) scale(2.2);opacity:0}100%{transform:rotate(-12deg) scale(1);opacity:1}}
@media (max-width:420px){.rga-daily__days{grid-template-columns:repeat(3,minmax(0,1fr))}
  .rga-daily__day[data-day="7"]{grid-column:span 3}}

/* ── end-of-match sigils ────────────────────────────────────────────── */
.rga-crowns{display:flex;flex-direction:column;align-items:center;gap:.7rem;
  padding:1rem 1.15rem;border:var(--rga-rim,2px) solid var(--color-border);border-radius:var(--rga-radius-panel,14px);background:var(--rga-face,none),var(--color-surface);box-shadow:var(--rga-lift-1),var(--rga-sheen)}
.rga-crowns__rows{display:flex;flex-direction:column;gap:.45rem;width:100%}
.rga-crowns__row{display:flex;align-items:center;justify-content:space-between;gap:1rem}
.rga-crowns__who{font-size:.82rem;font-weight:700;color:var(--color-text-dim)}
.rga-crowns__pips{display:flex;gap:.4rem}
.rga-crowns__pip{width:26px;height:26px;border-radius:7px;transform:rotate(45deg);
  border:2px solid var(--color-border);background:var(--color-surface-2)}
/* A filled pip carries a shape cue as well as a colour one. */
.rga-crowns__pip.is-filled{border-color:#a8761a;
  background:radial-gradient(circle at 35% 30%,#ffe9a8,#e0a92c 60%,#a8761a);
  box-shadow:0 0 12px rgba(255,184,0,.45)}
.rga-crowns__pip.is-filled.is-in{animation:rga-rw-pop 260ms cubic-bezier(.34,1.56,.64,1) both}
.rga-crowns__reason{margin:0;font-size:.82rem;color:var(--color-text-dim);text-align:center}

/* ── chest ──────────────────────────────────────────────────────────── */
.rga-chest{display:flex;flex-direction:column;align-items:center;gap:.85rem;padding:.5rem 0}
.rga-chest__stage{display:grid;place-items:center;width:min(60vw,11rem);height:min(60vw,11rem);
  padding:0;border:0;background:transparent;cursor:pointer}
.rga-chest__frame{width:100%;height:100%;object-fit:contain;image-rendering:auto}
/* The drawn fallback, for before the 3D render lands. Lid, body, band, lock. */
.rga-chest__drawn{position:relative;width:78%;height:62%}
.rga-chest__lid{position:absolute;left:0;right:0;top:0;height:42%;border-radius:14px 14px 4px 4px;
  border:3px solid #5a3a12;background:linear-gradient(#c98a34,#8f5a17);
  transform-origin:50% 100%;transition:transform 220ms cubic-bezier(.34,1.56,.64,1)}
.rga-chest__body{position:absolute;left:0;right:0;bottom:0;height:62%;border-radius:4px 4px 12px 12px;
  border:3px solid #5a3a12;background:linear-gradient(#a9701f,#7a4c11)}
.rga-chest__lock{position:absolute;left:50%;top:36%;width:22px;height:24px;transform:translateX(-50%);
  border-radius:5px;border:3px solid #5a3a12;background:linear-gradient(#ffe08a,#d9a63c)}
.rga-chest.is-open .rga-chest__lid{transform:rotate(-24deg) translateY(-6%)}
.rga-chest__stage.is-idle{animation:rga-rw-hop 1.8s ease-in-out infinite}
.rga-chest__rewards{display:flex;flex-direction:column;gap:.4rem;width:min(84vw,20rem)}
.rga-chest__row{display:flex;align-items:center;gap:.6rem;padding:.45rem .6rem;border-radius:12px;
  border:var(--rga-rim,2px) solid var(--color-border);background:var(--rga-face,none),var(--color-surface);box-shadow:var(--rga-lift-1),var(--rga-sheen)}
.rga-chest__row[hidden]{display:none!important}
.rga-chest__icon{position:relative;flex:0 0 auto;width:30px;height:30px;border-radius:8px;
  border:2px solid rgba(0,0,0,.4);background:radial-gradient(circle at 35% 30%,#ffe9a8,#e0a92c 62%,#a8761a);
  animation:rga-rw-shimmer 1.8s ease-in-out infinite}
.rga-chest__icon[data-rarity=epic]{background:radial-gradient(circle at 35% 30%,#e8c6ff,#8a4fd0 62%,#4a2280)}
.rga-chest__icon[data-rarity=mythic]{background:radial-gradient(circle at 35% 30%,#ffd0c6,#e0553c 62%,#7a1f10)}
.rga-chest__name{flex:1 1 auto;font-size:.85rem;font-weight:700}
.rga-chest__amt{font-size:.9rem;font-weight:800;color:var(--color-gold)}

/* ── the after-fight scene ──────────────────────────────────────────── */
.rga-post{position:absolute;inset:0;display:grid;place-items:center;padding:1.25rem;
  /* Static ground: docs/UI-STYLE.md keeps the battle background still. */
  background:radial-gradient(120% 80% at 50% 30%,color-mix(in srgb,var(--color-surface) 92%,transparent) 0%,color-mix(in srgb,var(--color-bg) 96%,transparent) 74%)}
/*
 * THE SKIP TARGET, AND WHY IT IS A SIBLING RATHER THAN THE STAGE.
 *
 * _errata/match-end.md E2: §3.4 puts the fast-forward press on the stage,
 * which CONTAINS every beat's control — so a press on Continue also presses the
 * stage (attachPress's release test is the stage's own rect, and the button is
 * inside it) and the scene advances twice. The scrim is a sibling UNDER the
 * stage: it cannot contain a control, so it cannot swallow one.
 *
 * Neither rule may set the border SHORTHAND. _CONTRACTS.md §6.5 rule 1 —
 * an unlayered border kills every Tailwind border utility on the same element,
 * and test/css-layer-guard.test.ts derives its class list from the sheet.
 */
.rga-post__scrim{position:absolute;inset:0;z-index:0;background:transparent}
.rga-post__stage{position:relative;z-index:1;
  display:flex;flex-direction:column;align-items:center;gap:1rem;
  width:min(92vw,24rem);max-height:100%;overflow:auto}
.rga-post__panel,.rga-post__celebration{display:flex;flex-direction:column;align-items:center;gap:.9rem;
  width:100%;padding:1.1rem 1.15rem;
  border:var(--rga-rim,2px) solid var(--color-border);border-radius:var(--rga-radius-panel,14px);background:var(--rga-face,none),var(--color-surface);box-shadow:var(--rga-lift-1),var(--rga-sheen)}
.rga-post__actions{display:flex;flex-wrap:wrap;justify-content:center;gap:.6rem;width:100%}
.rga-post__actions .rga-rw__cta{flex:1 1 8rem}
/* Secondary is a different SHAPE and weight, not just a different colour. */
.rga-rw__cta.is-secondary{color:var(--color-text);background:var(--rga-face,none),var(--color-surface-2);
  box-shadow:var(--rga-lift-2),var(--rga-sheen)}
/* One centred subject, as the reference's full-screen reward beat. */
.rga-post__subject{display:grid;place-items:center;min-height:160px}
.rga-post__art{width:160px;height:160px;object-fit:contain;animation:rga-rw-shimmer 1.8s ease-in-out infinite}
.rga-post__amount{margin:0;display:flex;align-items:baseline;gap:.5rem;font-weight:800}
.rga-post__amount-x{font-size:1.35rem;color:var(--color-gold)}
/*
 * The loss beat's Warpath bar (§3.3). The fill is NEVER the only cue —
 * STYLE_BIBLE §3 — so .rga-post__bar-read prints the same fact as a number
 * right underneath it. The duration is set inline per mount, not here, because
 * it is a fixed budget the caller can override for a test.
 */
.rga-post__bar{width:100%;height:14px;border-radius:8px;overflow:hidden;
  border:1px solid var(--color-border);background:var(--color-surface-2);box-shadow:var(--rga-depth)}
.rga-post__bar-fill{display:block;height:100%;width:0;border-radius:8px;
  background:linear-gradient(90deg,var(--color-gold),var(--color-gold-bright));
  transition-property:width;transition-timing-function:cubic-bezier(.2,.6,.3,1)}
.rga-post__bar-read{margin:0;font-size:.85rem;font-weight:700;font-variant-numeric:tabular-nums}

/* ── placeholder box ────────────────────────────────────────────────── */
.rga-ph{display:flex;flex-direction:column;align-items:center;gap:.35rem}
.rga-ph__box{border-radius:14px;border:3px dashed var(--color-ready);
  background-color:rgba(231,76,60,.06);
  background-image:repeating-linear-gradient(45deg,rgba(231,76,60,.18) 0 8px,transparent 8px 16px)}
.rga-ph__caption{font-size:.72rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
  color:var(--color-ready)}

/* ── keyframes: reward subjects only ────────────────────────────────── */
@keyframes rga-rw-pip{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.18);opacity:.75}}
@keyframes rga-rw-shimmer{0%,100%{filter:brightness(1)}50%{filter:brightness(1.45)}}
@keyframes rga-rw-pop{0%{transform:rotate(45deg) scale(.2);opacity:0}100%{transform:rotate(45deg) scale(1);opacity:1}}
@keyframes rga-rw-hop{0%,100%{transform:translateY(0) rotate(0)}
  40%{transform:translateY(-8%) rotate(-4deg)}70%{transform:translateY(0) rotate(3deg)}}

/* Both switches: the OS preference, and a caller that already resolved it. */
.rga-rw.is-reduced-motion *,.rga-rw.is-reduced-motion{animation:none!important;transition:none!important}
@media (prefers-reduced-motion:reduce){
  .rga-rw *,.rga-rw{animation:none!important;transition:none!important}
}
`;

let refs = 0;

/**
 * Inject the stylesheet once. Returns a release; the last release removes it.
 *
 * Safe to call from any number of components in any order, and safe to release
 * twice (the second is a no-op) so a component's teardown can be idempotent.
 */
export function injectRewardsCss(doc: Document = document): () => void {
  if (doc.getElementById(STYLE_ID) === null) {
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = REWARDS_CSS;
    (doc.head ?? doc.documentElement).appendChild(style);
    // A document that was torn down and rebuilt (every jsdom test file) starts
    // with no node, so the count belongs to the NODE, not to the module.
    refs = 0;
  }
  refs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refs = Math.max(0, refs - 1);
    if (refs === 0) doc.getElementById(STYLE_ID)?.remove();
  };
}
